/**
 * EditBlueprintModal â€” Excelencia Module
 *
 * Modal para edicao de blueprint existente.
 * Ao salvar alteracoes de conteudo (nome, descricao, etapas, categoria),
 * o service cria automaticamente um snapshot da versao anterior
 * e incrementa a versao do blueprint.
 *
 * Stages sao editadas via BlueprintStageEditor (accordion colapsavel).
 * CRUD real: persiste via excelenciaService.editBlueprint.
 */

import React, { useState, useCallback, useEffect } from 'react';
import {
  X,
  Plus,
  Trash2,
  Loader2,
  FileEdit,
  AlertCircle,
  GitBranch,
  Lock,
  Info,
  ChevronDown,
  ChevronUp,
} from 'lucide-react';
import type { ProcessBlueprint, BlueprintCategory, BlueprintStage } from '../../../types/processBlueprint';
import { CATEGORY_LABELS } from '../../../types/processBlueprint';
import { BlueprintStageEditor } from '../components/BlueprintStageEditor';

// ============================================================================
// TYPES
// ============================================================================

interface EditBlueprintModalProps {
  /** Blueprint a ser editado (null = modal fechado) */
  blueprint: ProcessBlueprint | null;
  /** Handler de fechamento */
  onClose: () => void;
  /** Handler de submissao (com versionamento automatico) */
  onSave: (
    blueprintId: string,
    changes: {
      name?: string;
      description?: string;
      category?: BlueprintCategory;
      stages?: BlueprintStage[];
    },
    changeSummary?: string,
  ) => Promise<void>;
}

// ============================================================================
// CATEGORY OPTIONS
// ============================================================================

const CATEGORY_OPTIONS = Object.entries(CATEGORY_LABELS).map(([value, label]) => ({
  value,
  label,
}));

// ============================================================================
// HELPERS
// ============================================================================

function createEmptyStage(order: number): BlueprintStage {
  return {
    order,
    name: '',
    description: '',
    slaHours: undefined,
    completionType: 'simple_complete',
  };
}

// ============================================================================
// COMPONENT
// ============================================================================

export const EditBlueprintModal: React.FC<EditBlueprintModalProps> = React.memo(
  ({ blueprint, onClose, onSave }) => {
    const [name, setName] = useState('');
    const [description, setDescription] = useState('');
    const [category, setCategory] = useState<string>('geral');
    const [stages, setStages] = useState<BlueprintStage[]>([]);
    const [changeSummary, setChangeSummary] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [expandedStageIndex, setExpandedStageIndex] = useState<number | null>(null);

    // Populate form when blueprint changes
    useEffect(() => {
      if (blueprint) {
        setName(blueprint.name);
        setDescription(blueprint.description ?? '');
        setCategory(blueprint.category ?? 'geral');
        setStages(blueprint.stages.map((s, i) => ({ ...s, order: s.order ?? i + 1 })));
        setChangeSummary('');
        setError(null);
        setExpandedStageIndex(null);
      }
    }, [blueprint]);

    const handleClose = useCallback(() => {
      setError(null);
      onClose();
    }, [onClose]);

    const addStage = useCallback(() => {
      setStages(prev => {
        const next = [...prev, createEmptyStage(prev.length + 1)];
        return next;
      });
      setExpandedStageIndex(stages.length);
    }, [stages.length]);

    const removeStage = useCallback((index: number) => {
      setStages(prev =>
        prev
          .filter((_, i) => i !== index)
          .map((s, i) => ({ ...s, order: i + 1 })),
      );
      setExpandedStageIndex(null);
    }, []);

    const updateStage = useCallback((index: number, updated: BlueprintStage) => {
      setStages(prev => prev.map((s, i) => (i === index ? { ...updated, order: i + 1 } : s)));
    }, []);

    const toggleStageExpansion = useCallback((index: number) => {
      setExpandedStageIndex(prev => (prev === index ? null : index));
    }, []);

    const handleSubmit = useCallback(async () => {
      if (!blueprint) return;
      if (!name.trim()) {
        setError('Nome do blueprint e obrigatorio');
        return;
      }

      const isManualSource = blueprint.sourceType === 'manual';

      // Only send stages for manual blueprints (imported stages are read-only)
      const validStages = isManualSource
        ? stages.filter(s => s.name.trim()).map((s, i) => ({ ...s, order: i + 1 }))
        : undefined;

      setIsSubmitting(true);
      setError(null);
      try {
        await onSave(
          blueprint.id,
          {
            name: name.trim(),
            description: description.trim() || undefined,
            category: category as BlueprintCategory,
            ...(validStages !== undefined ? { stages: validStages } : {}),
          },
          changeSummary.trim() || undefined,
        );
        handleClose();
      } catch (err: unknown) {
        setError(err instanceof Error ? err.message : 'Erro ao salvar blueprint');
      } finally {
        setIsSubmitting(false);
      }
    }, [blueprint, name, description, category, stages, changeSummary, onSave, handleClose]);

    if (!blueprint) return null;

    const isManual = blueprint.sourceType === 'manual';
    const isPublished = blueprint.status === 'published' || blueprint.status === 'standard';

    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
        <div className="w-full max-w-3xl bg-white dark:bg-[var(--bg-surface)] rounded shadow-2xl border border-gray-200 dark:border-slate-700 overflow-hidden flex flex-col" style={{ maxHeight: '90vh' }}>
          {/* Header */}
          <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 dark:border-slate-800 shrink-0">
            <div className="flex items-center gap-2">
              <FileEdit size={18} className="text-purple-500" />
              <h2 className="text-sm font-bold text-gray-900 dark:text-slate-100">
                {isPublished ? 'Editar (Nova Versao Draft)' : 'Editar Blueprint'}
              </h2>
              <span className="text-[10px] font-mono text-gray-400 bg-gray-100 dark:bg-slate-800 px-1.5 py-0.5 rounded">
                v{blueprint.versionSemver}
              </span>
            </div>
            <button
              onClick={handleClose}
              className="p-1.5 hover:bg-gray-100 dark:hover:bg-slate-800 rounded transition-colors"
            >
              <X size={16} className="text-gray-500" />
            </button>
          </div>

          {/* Published blueprint warning */}
          {isPublished && (
            <div className="px-5 py-2.5 bg-blue-50 dark:bg-blue-900/20 border-b border-blue-200 dark:border-blue-800 flex items-center gap-2 shrink-0">
              <Lock size={14} className="text-blue-600 dark:text-blue-400 flex-shrink-0" />
              <span className="text-[11px] text-blue-700 dark:text-blue-300">
                Blueprint publicado nao pode ser editado diretamente. Salvar criara um novo rascunho (draft) com versao incrementada.
              </span>
            </div>
          )}

          {/* Versioning warning (only for drafts) */}
          {!isPublished && (
            <div className="px-5 py-2.5 bg-amber-50 dark:bg-amber-900/20 border-b border-amber-200 dark:border-amber-800 flex items-center gap-2 shrink-0">
              <GitBranch size={14} className="text-amber-600 dark:text-amber-400 flex-shrink-0" />
              <span className="text-[11px] text-amber-700 dark:text-amber-300">
                Alteracoes de conteudo criam automaticamente uma nova versao semantica.
              </span>
            </div>
          )}

          {/* Form */}
          <div className="px-5 py-4 space-y-4 overflow-y-auto flex-1 min-h-0">
            {/* Error */}
            {error && (
              <div className="text-xs text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded p-2 flex items-center gap-2">
                <AlertCircle size={14} className="flex-shrink-0" />
                {error}
              </div>
            )}

            {/* Name */}
            <div>
              <label className="block text-xs font-medium text-gray-700 dark:text-slate-300 mb-1">
                Nome do Blueprint *
              </label>
              <input
                value={name}
                onChange={e => setName(e.target.value)}
                placeholder="Ex: Processo de Onboarding"
                className="w-full px-3 py-2 text-sm bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded focus:outline-none focus:ring-2 focus:ring-purple-500/30 text-gray-900 dark:text-slate-100 placeholder-gray-400"
              />
            </div>

            {/* Category */}
            <div>
              <label className="block text-xs font-medium text-gray-700 dark:text-slate-300 mb-1">
                Categoria
              </label>
              <select
                value={category}
                onChange={e => setCategory(e.target.value)}
                className="w-full px-3 py-2 text-sm bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded focus:outline-none focus:ring-2 focus:ring-purple-500/30 text-gray-900 dark:text-slate-100"
              >
                {CATEGORY_OPTIONS.map(opt => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Description */}
            <div>
              <label className="block text-xs font-medium text-gray-700 dark:text-slate-300 mb-1">
                Descricao
              </label>
              <textarea
                value={description}
                onChange={e => setDescription(e.target.value)}
                placeholder="Descricao opcional do processo..."
                rows={2}
                className="w-full px-3 py-2 text-sm bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded focus:outline-none focus:ring-2 focus:ring-purple-500/30 text-gray-900 dark:text-slate-100 placeholder-gray-400 resize-none"
              />
            </div>

            {/* Change summary */}
            <div>
              <label className="block text-xs font-medium text-gray-700 dark:text-slate-300 mb-1">
                Resumo das alteracoes (opcional)
              </label>
              <input
                value={changeSummary}
                onChange={e => setChangeSummary(e.target.value)}
                placeholder="Ex: Adicionada etapa de revisao"
                className="w-full px-3 py-2 text-sm bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded focus:outline-none focus:ring-2 focus:ring-purple-500/30 text-gray-900 dark:text-slate-100 placeholder-gray-400"
              />
            </div>

            {/* Stages */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-xs font-medium text-gray-700 dark:text-slate-300 flex items-center gap-1.5">
                  Etapas do Processo
                  {!isManual && <Lock size={11} className="text-gray-400 dark:text-slate-500" />}
                </label>
                {isManual && (
                  <button
                    onClick={addStage}
                    className="flex items-center gap-1 text-[11px] text-purple-600 dark:text-purple-400 hover:text-purple-700 dark:hover:text-purple-300 font-medium"
                  >
                    <Plus size={12} />
                    Adicionar Etapa
                  </button>
                )}
              </div>

              {/* Read-only notice for imported blueprints */}
              {!isManual && (
                <div className="mb-2 flex items-center gap-2 px-3 py-2 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded">
                  <Info size={13} className="text-blue-500 dark:text-blue-400 flex-shrink-0" />
                  <span className="text-[11px] text-blue-700 dark:text-blue-300">
                    Etapas importadas de {blueprint.sourceType === 'map' ? 'Mapa' : 'Workflow'} sao somente leitura.
                    Para alterar a estrutura, edite a fonte original e reimporte.
                  </span>
                </div>
              )}

              <div className="space-y-2">
                {stages.map((stage, idx) => {
                  const isExpanded = expandedStageIndex === idx;
                  return (
                    <div
                      key={idx}
                      className={`rounded border overflow-hidden ${
                        isManual
                          ? 'bg-gray-50 dark:bg-slate-800/50 border-gray-200 dark:border-slate-700'
                          : 'bg-gray-50/60 dark:bg-slate-800/30 border-gray-100 dark:border-slate-800 opacity-80'
                      }`}
                    >
                      {/* Stage header (always visible) */}
                      <div
                        className="flex items-center gap-2 px-3 py-2.5 cursor-pointer hover:bg-gray-100 dark:hover:bg-slate-700/30 transition-colors"
                        onClick={() => toggleStageExpansion(idx)}
                      >
                        <span className="text-[10px] font-mono text-gray-400 dark:text-slate-500 w-5 shrink-0 text-center">
                          {idx + 1}.
                        </span>
                        <span className={`flex-1 text-xs font-medium truncate ${
                          stage.name
                            ? 'text-gray-900 dark:text-slate-100'
                            : 'text-gray-400 dark:text-slate-500 italic'
                        }`}>
                          {stage.name || 'Etapa sem nome'}
                        </span>
                        {stage.slaHours != null && stage.slaHours > 0 && (
                          <span className="text-[10px] text-gray-400 dark:text-slate-500 font-mono shrink-0">
                            {stage.slaHours}h
                          </span>
                        )}
                        {stage.completionType === 'approve_reject' && (
                          <span className="text-[9px] bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 px-1.5 py-0.5 rounded font-medium shrink-0">
                            Aprovacao
                          </span>
                        )}
                        {((stage.formFields?.length ?? 0) > 0 || (stage.checklist?.length ?? 0) > 0) && (
                          <span className="text-[9px] bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-400 px-1.5 py-0.5 rounded font-medium shrink-0">
                            {[
                              (stage.formFields?.length ?? 0) > 0 ? `${stage.formFields!.length} campos` : '',
                              (stage.checklist?.length ?? 0) > 0 ? `${stage.checklist!.length} checks` : '',
                            ].filter(Boolean).join(' + ')}
                          </span>
                        )}
                        {isManual && (
                          <button
                            onClick={e => { e.stopPropagation(); removeStage(idx); }}
                            className="p-1 text-gray-400 hover:text-red-500 dark:hover:text-red-400 transition-colors shrink-0"
                            title="Remover etapa"
                          >
                            <Trash2 size={12} />
                          </button>
                        )}
                        {isExpanded ? (
                          <ChevronUp size={14} className="text-gray-400 dark:text-slate-500 shrink-0" />
                        ) : (
                          <ChevronDown size={14} className="text-gray-400 dark:text-slate-500 shrink-0" />
                        )}
                      </div>

                      {/* Stage editor (expanded) */}
                      {isExpanded && (
                        <div className="border-t border-gray-200 dark:border-slate-700 px-3 py-3">
                          <BlueprintStageEditor
                            stage={stage}
                            onChange={updated => updateStage(idx, updated)}
                            readOnly={!isManual}
                          />
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              {stages.length === 0 && (
                <div className="text-center py-4">
                  <p className="text-xs text-gray-400 dark:text-slate-500">
                    {isManual
                      ? 'Nenhuma etapa. Clique em "Adicionar Etapa" acima.'
                      : 'Nenhuma etapa importada.'}
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Footer */}
          <div className="px-5 py-3 border-t border-gray-200 dark:border-slate-800 flex justify-end gap-2 shrink-0">
            <button
              onClick={handleClose}
              disabled={isSubmitting}
              className="px-4 py-2 text-xs text-gray-600 dark:text-slate-400 hover:bg-gray-100 dark:hover:bg-slate-800 rounded transition-colors disabled:opacity-50"
            >
              Cancelar
            </button>
            <button
              onClick={handleSubmit}
              disabled={isSubmitting || !name.trim()}
              className={`flex items-center gap-1.5 px-4 py-2 text-xs font-medium text-white rounded transition-colors disabled:opacity-50 ${
                isPublished
                  ? 'bg-blue-600 hover:bg-blue-700'
                  : 'bg-purple-600 hover:bg-purple-700'
              }`}
            >
              {isSubmitting ? (
                <Loader2 size={12} className="animate-spin" />
              ) : isPublished ? (
                <GitBranch size={12} />
              ) : (
                <FileEdit size={12} />
              )}
              {isPublished ? 'Criar Nova Versao Draft' : 'Salvar Alteracoes'}
            </button>
          </div>
        </div>
      </div>
    );
  },
);

EditBlueprintModal.displayName = 'EditBlueprintModal';
